'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MileageCalculator() {
  const t = useTranslations("tools.mileageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    fromLocation: '',
    toLocation: '',
    fuelEfficiency: '',
    fuelPrice: ''
  });

  const [results, setResults] = useState<{
    distance: number;
    drivingTime: { hours: number; minutes: number };
    fuelNeeded: number | null;
    totalCost: number | null;
  } | null>(null);

  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const cityDistances: { [key: string]: { lat: number; lon: number } } = {
    'new york': { lat: 40.7128, lon: -74.0060 },
    'boston': { lat: 42.3601, lon: -71.0589 },
    'los angeles': { lat: 34.0522, lon: -118.2437 },
    'chicago': { lat: 41.8781, lon: -87.6298 },
    'houston': { lat: 29.7604, lon: -95.3698 },
    'phoenix': { lat: 33.4484, lon: -112.0740 },
    'philadelphia': { lat: 39.9526, lon: -75.1652 },
    'san antonio': { lat: 29.4241, lon: -98.4936 },
    'san diego': { lat: 32.7157, lon: -117.1611 },
    'dallas': { lat: 32.7767, lon: -96.7970 },
    'san francisco': { lat: 37.7749, lon: -122.4194 },
    'seattle': { lat: 47.6062, lon: -122.3321 },
    'miami': { lat: 25.7617, lon: -80.1918 },
    'atlanta': { lat: 33.7490, lon: -84.3880 },
    'denver': { lat: 39.7392, lon: -104.9903 },
    'washington': { lat: 38.9072, lon: -77.0369 },
    'las vegas': { lat: 36.1699, lon: -115.1398 },
    'portland': { lat: 45.5152, lon: -122.6784 },
    'detroit': { lat: 42.3314, lon: -83.0458 },
    'orlando': { lat: 28.5383, lon: -81.3792 }
  };

  const normalizeLocation = (location: string): string => {
    return location.toLowerCase().trim().replace(/,.*$/, '').trim();
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c * 1.25;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const fromNormalized = normalizeLocation(inputs.fromLocation);
    const toNormalized = normalizeLocation(inputs.toLocation);

    const fromCoords = cityDistances[fromNormalized];
    const toCoords = cityDistances[toNormalized];

    if (!fromCoords || !toCoords) {
      setError(t('errorLocationNotFound'));
      setResults(null);
      return;
    }

    const distance = calculateDistance(
      fromCoords.lat, fromCoords.lon,
      toCoords.lat, toCoords.lon
    );

    const drivingTimeDecimal = distance / 60;
    const hours = Math.floor(drivingTimeDecimal);
    const minutes = Math.round((drivingTimeDecimal - hours) * 60);

    let fuelNeeded = null;
    let totalCost = null;

    if (inputs.fuelEfficiency && parseFloat(inputs.fuelEfficiency) > 0) {
      fuelNeeded = distance / parseFloat(inputs.fuelEfficiency);

      if (inputs.fuelPrice && parseFloat(inputs.fuelPrice) > 0) {
        totalCost = fuelNeeded * parseFloat(inputs.fuelPrice);
      }
    }

    setResults({
      distance: Math.round(distance),
      drivingTime: { hours, minutes },
      fuelNeeded,
      totalCost
    });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
      outline: 'none'
    },
    {},
    {},
    {}
  );

  const smallTextStyle = {
    color: '#64748b',
    marginTop: '0.25rem',
    fontSize: '0.875rem'
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)'
    },
    { padding: '1rem 2rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle = {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid #dc2626',
    display: error ? 'block' : 'none'
  };

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#eff6ff',
      borderLeft: '4px solid #2563eb',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
      color: '#1e40af'
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0',
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary, textAlign: 'center' as const },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const resultCardStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    borderLeft: '4px solid #2563eb'
  };

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.75rem' }
  );

  const resultUnitStyle = {
    fontSize: '1rem',
    color: '#64748b',
    fontWeight: 500
  };

  const routeInfoStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
  };

  const routeInfoH4Style = {
    margin: '0 0 1rem 0',
    color: colors.textPrimary,
    fontSize: '1.1rem'
  };

  const routeDetailStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0'
  };

  const routeDetailLabelStyle = {
    color: '#64748b',
    fontWeight: 500
  };

  const routeDetailValueStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🗺️ {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="from-location" style={labelStyle}>
                <span style={{ marginRight: '0.5rem' }}>📍</span>{t('fromLocationLabel')}
              </label>
              <input
                type="text"
                id="from-location"
                placeholder={t('fromLocationPlaceholder')}
                value={inputs.fromLocation}
                onChange={(e) => setInputs({...inputs, fromLocation: e.target.value})}
                style={inputStyle}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="to-location" style={labelStyle}>
                <span style={{ marginRight: '0.5rem' }}>🎯</span>{t('toLocationLabel')}
              </label>
              <input
                type="text"
                id="to-location"
                placeholder={t('toLocationPlaceholder')}
                value={inputs.toLocation}
                onChange={(e) => setInputs({...inputs, toLocation: e.target.value})}
                style={inputStyle}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="fuel-efficiency" style={labelStyle}>
                ⛽ {t('fuelEfficiencyLabel')}
              </label>
              <input
                type="number"
                id="fuel-efficiency"
                placeholder={t('fuelEfficiencyPlaceholder')}
                value={inputs.fuelEfficiency}
                onChange={(e) => setInputs({...inputs, fuelEfficiency: e.target.value})}
                style={inputStyle}
                step="0.1"
                min="0"
              />
              <small style={smallTextStyle}>{t('fuelEfficiencyHint')}</small>
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="fuel-price" style={labelStyle}>
                💵 {t('fuelPriceLabel')}
              </label>
              <input
                type="number"
                id="fuel-price"
                placeholder={t('fuelPricePlaceholder')}
                value={inputs.fuelPrice}
                onChange={(e) => setInputs({...inputs, fuelPrice: e.target.value})}
                style={inputStyle}
                step="0.01"
                min="0"
              />
              <small style={smallTextStyle}>{t('fuelPriceHint')}</small>
            </div>

            <button type="submit" style={calculateButtonStyle}>
              {t('calculateButton')}
            </button>

            <div style={errorMessageStyle}>
              {error}
            </div>

            <div style={infoBoxStyle}>
              💡 <strong>{t('tipLabel')}</strong> {t('tipText')}
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultsGridStyle}>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('totalDistanceLabel')}</span>
                  <span style={resultValueStyle}>
                    {results.distance} <span style={resultUnitStyle}>{t('milesUnit')}</span>
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('estimatedTimeLabel')}</span>
                  <span style={resultValueStyle}>
                    {results.drivingTime.hours} <span style={resultUnitStyle}>{t('hoursUnit')}</span> {results.drivingTime.minutes} <span style={resultUnitStyle}>{t('minutesUnit')}</span>
                  </span>
                </div>

                {results.fuelNeeded !== null && (
                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('fuelNeededLabel')}</span>
                    <span style={resultValueStyle}>
                      {results.fuelNeeded.toFixed(1)} <span style={resultUnitStyle}>{t('gallonsUnit')}</span>
                    </span>
                  </div>
                )}

                {results.totalCost !== null && (
                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('estimatedCostLabel')}</span>
                    <span style={resultValueStyle}>
                      ${results.totalCost.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div style={routeInfoStyle}>
                <h4 style={routeInfoH4Style}>🛣️ {t('routeDetailsTitle')}</h4>
                <div style={routeDetailStyle}>
                  <span style={routeDetailLabelStyle}>{t('fromLabel')}</span>
                  <span style={routeDetailValueStyle}>{inputs.fromLocation}</span>
                </div>
                <div style={routeDetailStyle}>
                  <span style={routeDetailLabelStyle}>{t('toLabel')}</span>
                  <span style={routeDetailValueStyle}>{inputs.toLocation}</span>
                </div>
                <div style={routeDetailStyle}>
                  <span style={routeDetailLabelStyle}>{t('averageSpeedLabel')}</span>
                  <span style={routeDetailValueStyle}>{t('averageSpeedValue')}</span>
                </div>
                {inputs.fuelEfficiency && parseFloat(inputs.fuelEfficiency) > 0 && (
                  <div style={routeDetailStyle}>
                    <span style={routeDetailLabelStyle}>{t('fuelEfficiencyLabel')}</span>
                    <span style={routeDetailValueStyle}>{inputs.fuelEfficiency} {t('mpgUnit')}</span>
                  </div>
                )}
                {inputs.fuelPrice && parseFloat(inputs.fuelPrice) > 0 && (
                  <div style={routeDetailStyle}>
                    <span style={routeDetailLabelStyle}>{t('fuelPriceLabel')}</span>
                    <span style={routeDetailValueStyle}>${inputs.fuelPrice}/{t('galUnit')}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Text')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Text')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('businessUseTitle')}</h3>
        <p style={paragraphStyle}>{t('businessUseParagraph')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('fuelTrackingTitle')}</h3>
        <p style={paragraphStyle}>{t('fuelTrackingParagraph')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('complianceTitle')}</h3>
        <p style={paragraphStyle}>{t('complianceParagraph')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practice1')}</li>
          <li style={listItemStyle}>{t('practice2')}</li>
          <li style={listItemStyle}>{t('practice3')}</li>
          <li style={listItemStyle}>{t('practice4')}</li>
          <li style={listItemStyle}>{t('practice5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://kilometrowka.net/en/mileage-calculator-2/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.mileagewise.com/odometer-reading-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.v3cars.com/mileage-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.everlance.com/automatic-mileage-tracking" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.hurdlr.com/mileage-tracker" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}